import clsx from 'clsx';
import { Skeleton } from './Skeleton';
import { SkeletonStatsCard, SkeletonBlockCard, SkeletonProjeKarti } from './SkeletonCard';
import { SkeletonTable } from './SkeletonTable';

/**
 * DashboardSkeleton - Dashboard sayfası için tam skeleton
 */
export function DashboardSkeleton() {
    return (
        <div className="p-4 md:p-6 space-y-6 animate-pulse">
            {/* Header */}
            <div className="space-y-2">
                <Skeleton variant="text" className="h-7 w-40" />
                <Skeleton variant="text" className="h-4 w-28" />
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" className="h-16" />
                ))}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                    <SkeletonStatsCard key={i} />
                ))}
            </div>

            {/* Apartment Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                    <SkeletonStatsCard key={i} />
                ))}
            </div>

            {/* Alerts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                    <div key={i} className="bg-bg-secondary rounded-xl border border-border-light p-4">
                        <Skeleton variant="text" className="h-5 w-40 mb-4" />
                        <div className="space-y-3">
                            {[1, 2, 3].map((j) => (
                                <div key={j} className="flex items-center justify-between py-2">
                                    <div className="space-y-1">
                                        <Skeleton variant="text" className="h-4 w-28" />
                                        <Skeleton variant="text" className="h-3 w-20" />
                                    </div>
                                    <Skeleton variant="text" className="h-5 w-20" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * TablePageSkeleton - Tablo sayfaları için skeleton (Daireler, Kişiler, Firmalar, vb.)
 */
export function TablePageSkeleton({
    showStats = true,
    statsCount = 4,
    tableRows = 10,
    tableColumns = 6,
}) {
    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Stats */}
            {showStats && (
                <div className={clsx(
                    'grid gap-4',
                    statsCount <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-6'
                )}>
                    {Array.from({ length: statsCount }).map((_, i) => (
                        <SkeletonStatsCard key={i} />
                    ))}
                </div>
            )}

            {/* Table Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-2">
                    <Skeleton variant="text" className="h-6 w-28" />
                    <Skeleton variant="text" className="h-4 w-36" />
                </div>
                <div className="flex items-center gap-3">
                    <Skeleton variant="rounded" className="h-10 w-48" />
                    <Skeleton variant="rounded" className="h-10 w-28" />
                </div>
            </div>

            {/* Table */}
            <SkeletonTable rows={tableRows} columns={tableColumns} />
        </div>
    );
}

/**
 * CardGridSkeleton - Kart grid sayfaları için skeleton (İnşaatlar, Bloklar)
 */
export function CardGridSkeleton({
    variant = 'block', // 'block' | 'construction'
    cardCount = 6,
    showStats = true,
    columns = 3,
}) {
    const CardComponent = variant === 'construction' ? SkeletonProjeKarti : SkeletonBlockCard;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-2">
                    <Skeleton variant="text" className="h-6 w-28" />
                    <Skeleton variant="text" className="h-4 w-40" />
                </div>
                <Skeleton variant="rounded" className="h-10 w-28" />
            </div>

            {/* Stats */}
            {showStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <SkeletonStatsCard key={i} />
                    ))}
                </div>
            )}

            {/* Budget Bar (for constructions) */}
            {variant === 'construction' && (
                <div className="bg-bg-tertiary/50 rounded-2xl p-5 border border-border-light">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="text-center">
                                <Skeleton variant="text" className="h-3 w-20 mx-auto mb-2" />
                                <Skeleton variant="text" className="h-7 w-24 mx-auto" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Cards Grid */}
            <div className={clsx(
                'grid gap-4',
                columns === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            )}>
                {Array.from({ length: cardCount }).map((_, i) => (
                    <CardComponent key={i} />
                ))}
            </div>
        </div>
    );
}

/**
 * FinancialReportSkeleton - Gelir-Gider Raporu sayfası için skeleton
 */
export function FinancialReportSkeleton() {
    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="space-y-2">
                    <Skeleton variant="text" className="h-6 w-40" />
                    <Skeleton variant="text" className="h-4 w-32" />
                </div>
                <div className="flex items-center gap-3">
                    <Skeleton variant="rounded" className="h-10 w-28" />
                    <Skeleton variant="rounded" className="h-10 w-20" />
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-3">
                <Skeleton variant="rounded" className="h-12 w-32" />
                <Skeleton variant="rounded" className="h-12 w-32" />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-bg-secondary rounded-xl border border-border-light p-5">
                        <div className="flex items-center gap-3 mb-3">
                            <Skeleton variant="rounded" className="w-10 h-10" />
                            <Skeleton variant="text" className="h-4 w-24" />
                        </div>
                        <Skeleton variant="text" className="h-8 w-32" />
                    </div>
                ))}
            </div>

            {/* Distribution Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                    <div key={i} className="bg-bg-secondary rounded-xl border border-border-light p-5">
                        <Skeleton variant="text" className="h-5 w-32 mb-4" />
                        <div className="space-y-3">
                            {[1, 2, 3, 4].map((j) => (
                                <div key={j} className="flex items-center gap-3">
                                    <Skeleton variant="text" className="h-4 flex-1" />
                                    <Skeleton variant="text" className="h-4 w-16" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default {
    DashboardSkeleton,
    TablePageSkeleton,
    CardGridSkeleton,
    FinancialReportSkeleton,
};
